import React, { useEffect, useState } from 'react';
import { Box, Button, Container, FormControl, FormControlLabel, Radio, RadioGroup, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom'; // useNavigate 훅 추가
import { getPolicyAPI } from 'apis/policy/policyAPI';
import usePolicyStore from 'store/usePolicyStore';
import Tiptap from '../Tiptap/Tiptap';
import ScrollToTop from '../ScrollToTop'; // Zustand에서 상태를 가져옴
import { useTranslation } from 'react-i18next';

interface SignUpAgreeTermsProps {
  handleNext: () => void;
  handleBack: () => void;
}

/* 이용약관 동의 페이지 */
export default function SignUpAgreeTerms({ handleNext, handleBack }: SignUpAgreeTermsProps) {
  const navigate = useNavigate(); // useNavigate 훅 사용
  const { termsContent, privacyContent, setPolicy } = usePolicyStore(); // Zustand에서 가져옴 : 서버에서 받아온 기존 이용약관 및 개인정보처리방침
  // 동의 상태를 배열로 관리 (index 0: termsAgree, index 1: privacyAgree)
  const [agreements, setAgreements] = useState([false, false]);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation(); // useTranslation 훅 사용

  // 라디오 버튼 변경 핸들러
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const newAgreements = [...agreements]; // 상태 배열 복사

    if (name === 'terms-agree') {
      newAgreements[0] = value === 'true'; // 이용약관 동의 여부 업데이트
    } else if (name === 'privacy-agree') {
      newAgreements[1] = value === 'true'; // 개인정보 동의 여부 업데이트
    }

    setAgreements(newAgreements); // 상태 업데이트
  };

  const handleBackToHome = () => {
    navigate(-1); // 홈('/')으로 이동
  };

  // 데이터 가져오기 (컴포넌트 첫 마운트 시)
  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // 빈 배열이므로 처음 마운트될 때만 실행

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Zustand에 저장된 데이터가 없을 때만 API 호출
        const policyResponse = await getPolicyAPI();
        const { terms, termsPolicyId, privacy, privacyPolicyId } = policyResponse.data;
        setPolicy(terms, termsPolicyId, privacy, privacyPolicyId); // Zustand에 저장
      } catch (error) {
        console.error('데이터 가져오기 실패:', error);
      }
    };
    fetchSettings();
  }, [language]);

  return (
    <React.Fragment>
      <ScrollToTop />
      <Box display="flex" flexDirection="column" gap={2}>
        <FormControl>
          <Box
            width="100%"
            minWidth="400px"
            mt={4}
            mx="auto"
            padding={4}
            paddingBottom={2}
            borderRadius={1}
            bgcolor={theme.palette.mode === 'dark' ? '#333' : '#f8f8f8'}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <Typography variant="h5" fontWeight="bold">
              {t('sign_up_agree_terms.term')}
            </Typography>
            {/* Viewer에 termsContent 전달 */}
            {/* 데이터가 존재하지 않을 때 로딩 메시지 또는 스피너 표시 */}
            {!termsContent ? (
              <Typography>{t('sign_up_agree_terms.term_loading')}</Typography>
            ) : (
              <Box boxShadow={1} borderRadius={1} bgcolor="#fff">
                <Tiptap content={termsContent} editable={false} maxHeight="600px" />
              </Box>
            )}

            <RadioGroup
              row
              aria-labelledby="agree-terms-radio-group-label"
              name="terms-agree" // 각각의 RadioGroup에 서로 다른 name 속성 사용
              value={String(agreements[0])} // 현재 상태를 문자열로 변환하여 value로 설정
              onChange={handleRadioChange} // 라디오 버튼의 변경 핸들러
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <FormControlLabel value="true" control={<Radio />} label={t('sign_up_agree_terms.btn.agree')} />
                <FormControlLabel value="false" control={<Radio />} label={t('sign_up_agree_terms.btn.disagree')} />
              </Box>
            </RadioGroup>
          </Box>
        </FormControl>

        {/* 개인정보 수집 및 이용 동의 */}
        <FormControl>
          <Box
            width="100%"
            minWidth="400px"
            mt={4}
            mx="auto"
            padding={4}
            paddingBottom={2}
            borderRadius={1}
            bgcolor={theme.palette.mode === 'dark' ? '#333' : '#f8f8f8'}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <Typography variant="h5" fontWeight="bold">
              {t('sign_up_agree_terms.privacy')}
            </Typography>
            {/*
            Viewer에 privacyContent 전달  */}
            {/* 데이터가 존재하지 않을 때 로딩 메시지 또는 스피너 표시 */}
            {!privacyContent ? (
              <Typography>{t('sign_up_agree_terms.privacy_loading')}</Typography>
            ) : (
              <Box boxShadow={1} borderRadius={1} bgcolor="#fff">
                <Tiptap content={privacyContent} editable={false} maxHeight="600px" />
              </Box>
            )}

            <RadioGroup
              row
              aria-labelledby="agree-privacy-radio-group-label"
              name="privacy-agree" // 각각의 RadioGroup에 서로 다른 name 속성 사용
              value={String(agreements[1])} // 현재 상태를 문자열로 변환하여 value로 설정
              onChange={handleRadioChange} // 라디오 버튼의 변경 핸들러
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <FormControlLabel value="true" control={<Radio />} label={t('sign_up_agree_terms.btn.agree')} />
                <FormControlLabel value="false" control={<Radio />} label={t('sign_up_agree_terms.btn.disagree')} />
              </Box>
            </RadioGroup>
          </Box>
        </FormControl>

        {/* 이전 및 다음 버튼 */}
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, width: '100%' }}>
          <Button variant="contained" color="primary" onClick={handleBackToHome} sx={{ width: '100px', borderRadius: '4px' }}>
            {t('sign_up_agree_terms.btn.before')}
          </Button>

          <Box sx={{ flex: '1 1 auto' }} />
          {/* 두개의 동의가 모두 true일 때만 다음 버튼 활성화 */}

          <Button
            color="primary"
            variant="contained"
            onClick={handleNext}
            disabled={!agreements[0] || !agreements[1]}
            sx={{ width: '100px', borderRadius: '4px', marginLeft: '10px' }}
          >
            {t('sign_up_agree_terms.btn.after')}
          </Button>
        </Box>
      </Box>
    </React.Fragment>
  );
}
